/* eslint-disable ember/no-runloop */
import { set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { task, timeout } from 'ember-concurrency';
import { t } from 'ember-intl';
import { COLOR_PARITY_LOSS } from 'frontend-utils/utils/css-variable';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import { TOOLTIP_HOVER_TIMEOUT } from 'lumen/services/usage-analytics';

import styles from './chart-issue-type.module.css';

const allowedIssues = [
  'LOSS_SOLDOUT',
  'LOSS_ROOMTYPE',
  'LOSS_PRICE_DIFFERENCE',
];

interface ParityChartsChartIssueTypeSignature {
  Args: {
    hideHotelsMetric: unknown;
    issueTypeData: unknown;
    showLoadingPlaceholderState: unknown;
  };
  Element: HTMLElement;
}

export default class ParityChartsChartIssueType extends Component<ParityChartsChartIssueTypeSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];

  @service declare usageAnalytics: Services['usage-analytics'];

  trackHoverTask = task({ restartable: true }, async (issueType) => {
    await timeout(TOOLTIP_HOVER_TIMEOUT);
    this.usageAnalytics.track('Issue Type Details Viewed', {
      eventTrigger: 'issue-type-chart',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      issueType,
    });
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    if (this.args.issueTypeData) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const allIssues = this.args.issueTypeData
        // @ts-expect-error: Incorrect type
        .filter(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          (issue) => allowedIssues.indexOf(issue.segmentationValueId) !== -1,
        )
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort((a, b) => b.calculatedValues.loss - a.calculatedValues.loss);
      // @ts-expect-error: Incorrect type
      const series = [];

      if (allIssues) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        allIssues.forEach((issue, index) => {
          series.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            name: issue.segmentationValueId,
            data: [
              {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                x: index,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                y: issue.calculatedValues.loss,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                issue,
              },
            ],
            color: `var(${COLOR_PARITY_LOSS})`,
            borderColor: 'var(--prism-color-border-inverse-emphasis)',
          });
        });

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return series;
      }
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        zoomType: null,
        type: 'bar',
        title: '',
        height: null, // Grow chart to fill container
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
      },

      title: {
        text: 'parity-issuetype-chart',
      },

      legend: {
        enabled: false,
      },

      tooltip: {
        shared: false,
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shape: 'square',
        shadow: false,
        outside: true,
        distance: 20,

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();

          const tooltipValues = {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            totalNbOfShops: this.point.issue.value.loss,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            totalNbOfHotels: this.point.issue.value.subscriptions,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            singleValue: this.point.issue.calculatedValues.loss,
            singleValueTitle: that.intl
              .t('parity_analytics.percentage_total')
              .toString(),
            tooltipTitle: that.intl
              .t(
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                `parity_analytics.issue_type.${this.point.issue.segmentationValueId.toLowerCase()}`,
              )
              .toString(),
          };

          set(that, 'tooltipValues', tooltipValues);
          end();
          // @ts-expect-error: Incorrect type
          return document.getElementById('dummy-tooltip-for-issue-type')
            .innerHTML;
        },
        useHTML: true,
      },

      xAxis: {
        minPadding: 0.5,
        maxPadding: 0.5,
        tickInterval: 1,
        labels: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // Return translated issue type when available
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (this.chart.series[this.value]) {
              return that.intl
                .t(
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  `parity_analytics.issue_type.${this.chart.series[this.value].name.toLowerCase()}`,
                )
                .toString();
            }

            return '';
          },
        },
      },

      yAxis: {
        min: 0,
        title: {
          enabled: false,
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return that.intl.formatNumber(this.value, { style: 'percent' });
          },
        },
      },

      plotOptions: {
        series: {
          pointPadding: 0.1,
          groupPadding: 0.1,
          pointWidth: null,
          stacking: 'normal',
        },
        bar: {
          point: {
            events: {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              mouseOver() {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-member-access
                that.trackHoverTask.perform(this.issue.segmentationValueId);
              },
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              mouseOut() {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                that.trackHoverTask.cancelAll();
              },
            },
          },
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get knownIssueTypes() {
    if (this.args.issueTypeData) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return this.args.issueTypeData.map(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        (issue) => issue.segmentationValueId,
      );
    }

    return [];
  }

  <template>
    <PrismCard ...attributes>
      <:header as |Header|>
        <div class="flex items-center">
          {{#if @showLoadingPlaceholderState}}
            <p class="loading-pulse--col-3"></p>
          {{else}}
            <Header.title>
              {{t "parity_analytics.issue_type"}}
            </Header.title>
          {{/if}}
        </div>
      </:header>

      <:content>
        <div class={{styles.breakdown-content}}>
          <GeneralChartsOtaChart
            {{! @glint-expect-error: Incorrect type }}
            @chartOptions={{this.chartOptions}}
            @model={{this.chartData}}
            class="h-full"
          />
        </div>
      </:content>
    </PrismCard>
    <div class="hidden" id="dummy-tooltip-for-issue-type">
      <ParityTooltipsTooltipKpi
        {{! @glint-expect-error: Incorrect type }}
        @data={{this.tooltipValues}}
        {{! @glint-expect-error: Incorrect type }}
        @displayName={{this.tooltipValues.tooltipTitle}}
        {{! @glint-expect-error: Incorrect type }}
        @hideShoppedHotels={{@hideHotelsMetric}}
      />
    </div>
  </template>
}
